import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { HOME_LANGS } from "@/i18n/seo";
import { applySeoHead } from "@/lib/seoHead";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { About } from "@/components/site/About";
import { GoodToKnow } from "@/components/site/GoodToKnow";
import { Lounges } from "@/components/site/Lounges";
import { Families } from "@/components/site/Families";
import { Activities } from "@/components/site/Activities";
import { MenuSection } from "@/components/site/MenuSection";
import { InstagramSection } from "@/components/site/InstagramSection";
import { Soundtrack } from "@/components/site/Soundtrack";
import { Gallery } from "@/components/site/Gallery";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { BookingProvider } from "@/components/site/BookingModal";
import { AuthProvider } from "@/booking/AuthContext";
import BookPage from "@/pages/BookPage";
import ManageBooking from "@/pages/ManageBooking";
import BookingsPage from "@/pages/admin/BookingsPage";
import BeachCalendarPage from "@/pages/admin/BeachCalendarPage";
import RevenuePage from "@/pages/admin/RevenuePage";
import RegularsPage from "@/pages/admin/RegularsPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import ServicesPage from "@/pages/admin/ServicesPage";
import ProvidersPage from "@/pages/admin/ProvidersPage";
import ResourcesPage from "@/pages/admin/ResourcesPage";
import StatusesPage from "@/pages/admin/StatusesPage";
import SettingsPage from "@/pages/admin/SettingsPage";

const MarketingSite = () => {
  const { lng } = useParams();
  const valid = !lng || HOME_LANGS.includes(lng);
  const lang = valid && lng ? lng : "en";
  const { setLang } = useLang();

  useEffect(() => {
    setLang(lang);
    applySeoHead(lang);
  }, [lang, setLang]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  if (!valid) return <Navigate to="/" replace />;

  return (
    <BookingProvider>
      <div className="App">
        <Header />
        <main>
          <Hero />
          <About />
          <GoodToKnow />
          <Lounges />
          <Families />
          <Activities />
          <MenuSection />
          <InstagramSection />
          <Soundtrack />
          <Gallery />
          <Contact />
        </main>
        <Footer />
        <WhatsAppButton />
      </div>
    </BookingProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/:lng?" element={<MarketingSite />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/book/manage/:token" element={<ManageBooking />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<BeachCalendarPage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="revenue" element={<RevenuePage />} />
            <Route path="regulars" element={<RegularsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="statuses" element={<StatusesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
